import FeedbackGateToggle from '../../components/admin/FeedbackGateToggle';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import toast from 'react-hot-toast';
import { analyticsAPI, departmentsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import './DashboardPage.css';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, Title, Tooltip, Legend, Filler
);

const COLORS = ['#211D70','#E9972F','#76C2E5','#ED1C24','#FCB017','#FAC92E','#4C51BF','#7C3AED'];

export default function DashboardPage() {
  const { isAboutu }                  = useAuth();
  const [data, setData]               = useState(null);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [exporting, setExporting]     = useState(false);
  const [error, setError]             = useState(false);
  const [filters, setFilters]         = useState({ year: '', department_id: '' });
  const [commentSearch, setCommentSearch] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [analyticsRes, deptRes] = await Promise.all([
        analyticsAPI.get(filters),
        departmentsAPI.getAll(),
      ]);
      setData(analyticsRes.data);
      setDepartments(deptRes.data.departments);
    } catch {
      toast.error('Failed to load analytics');
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await analyticsAPI.exportCSV({
        year:          filters.year || undefined,
        department_id: filters.department_id || undefined,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a   = document.createElement('a');
      a.href    = url;
      a.download = `feedback_${filters.year || 'all'}.csv`;
      a.click();
      toast.success('CSV exported');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  // ── Chart data ────────────────────────────────────────────────────────────
  const avgRatingChart = data ? {
    labels: data.avgRatings.map((r) => r.name),
    datasets: [{
      label: 'Average Rating',
      data:  data.avgRatings.map((r) => parseFloat(r.avg_rating) || 0),
      backgroundColor: data.avgRatings.map((_, i) => COLORS[i % COLORS.length] + 'cc'),
      borderColor:     data.avgRatings.map((_, i) => COLORS[i % COLORS.length]),
      borderWidth: 2,
      borderRadius: 8,
    }],
  } : null;

  const trendChart = data ? {
    labels: data.trend.map((r) => r.year),
    datasets: [{
      label: 'Avg Rating',
      data:  data.trend.map((r) => parseFloat(r.avg_rating)),
      borderColor:     '#211D70',
      backgroundColor: 'rgba(33,29,112,0.08)',
      fill: true,
      tension: 0.4,
      pointBackgroundColor: '#E9972F',
      pointBorderColor:     '#fff',
      pointBorderWidth: 2,
      pointRadius: 6,
    }],
  } : null;

  const deptNames  = [...new Set((data?.distribution || []).map((r) => r.department))];
  const distChart  = deptNames.length > 0 ? {
    labels: ['1 ★', '2 ★', '3 ★', '4 ★', '5 ★'],
    datasets: deptNames.map((dept, i) => ({
      label: dept,
      data: [1,2,3,4,5].map((rating) => {
        const match = data.distribution.find(
          (r) => r.department === dept && parseInt(r.rating) === rating
        );
        return match ? parseInt(match.count) : 0;
      }),
      backgroundColor: COLORS[i % COLORS.length] + 'bb',
      borderColor:     COLORS[i % COLORS.length],
      borderWidth: 1.5,
      borderRadius: 4,
    })),
  } : null;

  const chartOptions = (maxY) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { font: { family: 'DM Sans', size: 12 }, padding: 16 } },
      tooltip: { bodyFont: { family: 'DM Sans' }, titleFont: { family: 'DM Sans' } },
    },
    scales: {
      x: { grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { font: { family: 'DM Sans', size: 11 } } },
      y: {
        min: 0, max: maxY,
        grid: { color: 'rgba(0,0,0,0.05)' },
        ticks: { font: { family: 'DM Sans', size: 11 } },
      },
    },
  });

  // ── Filtered comments ─────────────────────────────────────────────────────
  const allComments = data?.comments || [];
  const filteredComments = commentSearch.trim()
    ? allComments.filter((c) =>
        c.comment.toLowerCase().includes(commentSearch.toLowerCase()) ||
        c.department.toLowerCase().includes(commentSearch.toLowerCase())
      )
    : allComments;

  // ── Error state ───────────────────────────────────────────────────────────
  if (error || (!loading && !data)) {
    return (
      <div className="page-container" style={{ paddingTop: 32 }}>
        <div style={{ textAlign: 'center', padding: '80px 24px' }}>
          <div style={{ fontSize: '3rem', marginBottom: 16 }}>⚠️</div>
          <h2 style={{ color: 'var(--primary)', marginBottom: 8 }}>Could not load analytics</h2>
          <p className="text-muted" style={{ marginBottom: 24 }}>
            The backend may not be running, or there was a network error.
          </p>
          <button className="btn btn-primary" onClick={loadData}>Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" style={{ paddingTop: 32, paddingBottom: 48 }}>

      {/* Page header */}
      <div className="page-header flex-between" style={{ flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1>Analytics Dashboard</h1>
          {isAboutu && data?.lockedDepartment ? (
            <div className="dept-locked-banner">
              <span>🏢</span>
              <span>Viewing feedback for <strong>{data.lockedDepartment.name}</strong> department only</span>
            </div>
          ) : (
            <p className="text-muted">Feedback insights and trends</p>
          )}
        </div>
        <button className="btn btn-accent" onClick={handleExport} disabled={exporting}>
          {exporting ? 'Exporting…' : '⬇ Export CSV'}
        </button>
      </div>

        {/* ← ADD FeedbackGateToggle HERE */}
      <FeedbackGateToggle />

      {/* Filters — full for admin, year-only for aboutu */}
      <div className="dashboard-filters card"></div>

      {/* Filters — full for admin, year-only for aboutu */}
      <div className="dashboard-filters card">
        <div className="card-body" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ flex: '1 1 180px' }}>
            <label className="form-label">Year</label>
            <select
              className="form-select"
              value={filters.year}
              onChange={(e) => setFilters({ ...filters, year: e.target.value })}
            >
              <option value="">All Years</option>
              {(data?.availableYears || []).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          {/* Department filter — hidden for aboutu */}
          {!isAboutu && (
            <div className="form-group" style={{ flex: '1 1 220px' }}>
              <label className="form-label">Administrative & Support services</label>
              <select
                className="form-select"
                value={filters.department_id}
                onChange={(e) => setFilters({ ...filters, department_id: e.target.value })}
              >
                <option value="">All Administrative & Support services</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
          )}

          {!isAboutu && (
            <button
              className="btn btn-ghost"
              onClick={() => setFilters({ year: '', department_id: '' })}
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex-center" style={{ height: 300 }}><div className="spinner" /></div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="summary-grid animate-fade">
            <SummaryCard label="Total Submissions"   value={data?.summary?.total_submissions  || 0}                      icon="📋" color="var(--primary)" />
            <SummaryCard label="Unique Respondents"  value={data?.summary?.unique_respondents  || 0}                      icon="👥" color="var(--accent)" />
            <SummaryCard label="Overall Avg Rating"  value={data?.summary?.overall_avg ? `${data.summary.overall_avg} / 5` : 'N/A'} icon="⭐" color="var(--amber)" />
            <SummaryCard label="Total Comments"      value={allComments.length}                                            icon="💬" color="var(--sky)" />
          </div>

          {/* Charts grid */}
          <div className="chart-grid animate-fade">

            {/* Avg rating per dept */}
            <div className="chart-card card">
              <div className="card-header"><h3>Average Rating</h3></div>
              <div className="card-body chart-body">
                {avgRatingChart && avgRatingChart.labels.length > 0
                  ? <Bar data={avgRatingChart} options={chartOptions(5)} />
                  : <EmptyChart />}
              </div>
            </div>

            {/* Trend */}
            <div className="chart-card card">
              <div className="card-header"><h3>Rating Trend Over Years</h3></div>
              <div className="card-body chart-body">
                {trendChart && trendChart.labels.length > 0
                  ? <Line data={trendChart} options={chartOptions(5)} />
                  : <EmptyChart />}
              </div>
            </div>

            {/* Distribution */}
            <div className="chart-card chart-card--wide card">
              <div className="card-header"><h3>Rating Distribution</h3></div>
              <div className="card-body chart-body">
                {distChart && distChart.datasets.length > 0
                  ? <Bar data={distChart} options={chartOptions(undefined)} />
                  : <EmptyChart />}
              </div>
            </div>

            {/* Rankings table */}
            <div className="chart-card card">
              <div className="card-header"><h3>Administrative & Support Services Ranking</h3></div>
              <div className="card-body" style={{ padding: 0 }}>
                <table className="rank-table">
                  <thead>
                    <tr><th>#</th><th>Administrative & Support Services</th><th>Avg Rating</th><th>Responses</th></tr>
                  </thead>
                  <tbody>
                    {[...(data?.avgRatings || [])]
                      .filter((r) => r.avg_rating)
                      .sort((a, b) => b.avg_rating - a.avg_rating)
                      .map((r, i) => (
                        <tr key={r.id}>
                          <td className="rank-num">{r.rating_rank ?? i + 1}</td>
                          <td>{r.name}</td>
                          <td>
                            <span className="rating-pill" style={{ background: getRatingColor(r.avg_rating) }}>
                              ★ {r.avg_rating}
                            </span>
                          </td>
                          <td className="text-muted">{r.total_responses}</td>
                        </tr>
                      ))}
                    {(data?.avgRatings || []).filter((r) => r.avg_rating).length === 0 && (
                      <tr>
                        <td colSpan={4} style={{ textAlign: 'center', padding: 24, color: 'var(--text-light)' }}>
                          No data yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Comments Section ── */}
            <div className="chart-card chart-card--wide card">
              <div className="card-header flex-between" style={{ flexWrap: 'wrap', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <h3>💬 Feedback Comments</h3>
                  <span className="badge badge-primary">
                    {filteredComments.length} comment{filteredComments.length !== 1 ? 's' : ''}
                  </span>
                </div>
                {allComments.length > 0 && (
                  <input
                    className="form-input"
                    style={{ maxWidth: 260, fontSize: '0.85rem', padding: '6px 12px' }}
                    placeholder="Search comments or departments…"
                    value={commentSearch}
                    onChange={(e) => setCommentSearch(e.target.value)}
                  />
                )}
              </div>

              <div className="card-body" style={{ padding: 0 }}>
                {filteredComments.length === 0 ? (
                  <div className="comments-empty">
                    {allComments.length === 0
                      ? 'No comments submitted yet for the selected filters.'
                      : `No comments match "${commentSearch}"`}
                  </div>
                ) : (
                  <div className="comments-list">
                    {filteredComments.map((c, i) => (
                      <div key={i} className="comment-row">
                        <div className="comment-meta">
                          <span className="comment-dept">{c.department}</span>
                          <span className="comment-stars">
                            {[1,2,3,4,5].map((s) => (
                              <span key={s} style={{ color: s <= c.rating ? '#FCB017' : '#ddd', fontSize: '1rem' }}>
                                ★
                              </span>
                            ))}
                          </span>
                          <span className="comment-year text-muted text-small">{c.year}</span>
                        </div>
                        <p className="comment-text">{c.comment}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

          </div>
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, icon, color }) {
  return (
    <div className="summary-card card">
      <div className="card-body">
        <div className="summary-icon" style={{ background: color + '18', color }}>{icon}</div>
        <div>
          <div className="summary-value" style={{ color }}>{value}</div>
          <div className="summary-label">{label}</div>
        </div>
      </div>
    </div>
  );
}

function EmptyChart() {
  return (
    <div className="flex-center" style={{ height: '100%', color: 'var(--text-light)', fontSize: '0.9rem' }}>
      No data for selected filters
    </div>
  );
}

function getRatingColor(rating) {
  if (rating >= 4.5) return 'rgba(34,197,94,0.15)';
  if (rating >= 3.5) return 'rgba(252,176,23,0.15)';
  if (rating >= 2.5) return 'rgba(233,151,47,0.15)';
  return 'rgba(237,28,36,0.12)';
}
